/* eslint-disable @typescript-eslint/no-explicit-any */
// `react-table` handles keys on its own via prop getters.
/* eslint-disable react/jsx-key, react/no-array-index-key */
import { ControlledMenu, MenuItem, useMenuState } from '@szhsin/react-menu';
import type { ComponentProps, MouseEventHandler, ReactElement } from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { Column } from 'react-table';
import { useFlexLayout, useRowSelect, useTable } from 'react-table';

import { SortIndicator } from './components/SortIndicator';
import { getLeadsExportModalTitle } from './helpers/getLeadsExportModalTitle';
import {
    StyledTable,
    TableBody,
    TableHead,
    TableBodyRow,
    TableBodyCell,
    TableHeadCell,
    TableHeadTitle,
    TableHeadRow,
    TableLoadingRow,
    TableLoadingContent,
    TableEmptyState,
    TableHeadData,
} from './styled';

import { Columns } from '../../constants/columns';
import { ComponentIDS } from '../../constants/ids';
import { useWindowSize } from '../../hooks/useWindowSize';
import type { ILead, ILeadsSortParams } from '../../types/lead';
import { BottomToast } from '../BottomToast';
import { Button, ButtonVariant } from '../Button';
import { IconName } from '../Icon';
import { IconButton } from '../IconButton';
import { ExportModal } from '../Modal/components/ExportModal';

interface IMinimumTableData<TStatus> {
    [Columns.ID]: string | number;
    status?: TStatus;
}

interface IClickProps {
    columnName: string;
    functor: (id: string) => MouseEventHandler | undefined;
}

interface ITable<
    TStatus extends string = string,
    TData extends IMinimumTableData<TStatus> = IMinimumTableData<TStatus>,
    TExportData extends { [key: string]: unknown } = {
        [key: string]: unknown;
    }
> {
    tableData: TData[];
    tableColumns: Array<Column<TData>>;
    tableHiddenColumns: { [key: string]: string };
    tableCheckboxes: Column<object>;
    tableRightAlignedHeaderCellIds?: string[];
    dataGroup?: string;
    fetchExportData?: () => Promise<any[]>;
    nrOfResults?: number;
    showDnc?: boolean;
    emptyMessage: string;
    isAllSelected?: boolean;
    canLoadMore?: boolean;
    isLoadingInitially?: boolean;
    menuStatuses: TStatus[][];
    clickProps?: IClickProps;
    onReachedEnd?: () => void;
    onSelectAll?: (isSelected: boolean) => void;
    onSortApply?: (sortParams?: ILeadsSortParams) => void;
    onUpdateStatuses: (props: { ids: Array<ILead['leadId']>; newStatus: TStatus }) => void;
    prepareExportData: (dataToExport: TData[], showDnc: boolean | undefined) => TExportData[];
    prepareSelectAllExportData: (dataToExport: any[], showDnc: boolean | undefined) => any[];
    renderTableFilter?: () => ReactElement | null;

    /**
     * Enables hiding of portal on the top of the page
     */
    hideHeaderElement?: boolean;

    /**
     * Enables hiding of portal on the bottom of the page
     */
    hideFooterElement?: boolean;
}

const REACHED_END_THRESHOLD = 150;

export function Table<
    TStatus extends string = string,
    TData extends IMinimumTableData<TStatus> = IMinimumTableData<TStatus>,
    TExportData extends { [key: string]: unknown } = {
        [key: string]: unknown;
    }
>({
    tableData,
    tableColumns,
    tableHiddenColumns,
    tableCheckboxes,
    tableRightAlignedHeaderCellIds,
    dataGroup,
    fetchExportData,
    nrOfResults,
    nrOfLoadedResults,
    emptyMessage,
    menuStatuses,
    isAllSelected,
    isLoadingInitially,
    canLoadMore,
    clickProps,
    showDnc,
    onReachedEnd,
    onSelectAll,
    onUpdateStatuses,
    onSortApply,
    prepareExportData,
    prepareSelectAllExportData,
    renderTableFilter,
    hideHeaderElement,
    hideFooterElement,
    ...containerProps
}: ITable<TStatus, TData, TExportData> & ComponentProps<typeof StyledTable>) {
    const [updateLoading, setUpdateLoading] = useState(false);
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [sortParams, setSortParams] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' | undefined } | undefined>(
        undefined
    );
    const { toggleMenu: toggleIsMarkAsOpen, ...menuProps } = useMenuState({
        transition: true,
    });
    const markAsRef = useRef(null);
    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const headerElement = document.getElementById(ComponentIDS.HEADER);
    const { height } = useWindowSize();
    const { top } = tableBodyRef.current?.getBoundingClientRect() ?? {
        top: 0,
    };
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        headers,
        rows,
        prepareRow,
        selectedFlatRows,
        isAllRowsSelected,
        toggleAllRowsSelected,
    } = useTable<TData>(
        {
            columns: tableColumns,
            data: tableData,
            // we need to hide columns we want to export but not display
            initialState: {
                hiddenColumns: Object.values(tableHiddenColumns),
            },
        },
        useRowSelect, // Allows rows selection,
        useFlexLayout, // Allows flex layout
        hooks => {
            // This hooks is needed for the actual checkboxes to be added
            hooks.visibleColumns.push(columns => [
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tableCheckboxes as any,
                ...columns,
            ]);
        }
    );

    // if we select all -> we fetch all the leads from the API
    // so we can mark or export all leads, not just downloaded
    useEffect(() => {
        onSelectAll?.(isAllRowsSelected);
    }, [isAllRowsSelected]);

    // pre select all checkboxes
    useEffect(() => {
        if (isAllSelected && tableData) {
            toggleAllRowsSelected(true);
        }
    }, [isAllSelected, toggleAllRowsSelected, tableData]);

    const handleSort = (columndId: string) => {
        let newSortOrder: 'asc' | 'desc' | undefined;

        if (sortParams?.sortOrder === undefined) {
            newSortOrder = 'asc';
        } else if (sortParams?.sortOrder === 'asc') {
            newSortOrder = 'desc';
        } else {
            newSortOrder = undefined;
        }

        setSortParams(prevState => {
            return {
                sortBy: columndId,
                sortOrder: prevState?.sortBy === columndId ? newSortOrder : 'asc',
            };
        });

        onSortApply?.(
            newSortOrder && {
                sortBy: columndId,
                sortOrder: sortParams?.sortBy === columndId ? newSortOrder : 'asc',
            }
        );
    };

    const rowsToBeExported = selectedFlatRows.length ? selectedFlatRows.map(row => row.original) : undefined;

    const preparedExportData = async () => {
        if (!isAllSelected && rowsToBeExported) {
            return prepareExportData(rowsToBeExported, showDnc);
        }
        const fetchedData = fetchExportData ? await fetchExportData() : [];

        return prepareSelectAllExportData(fetchedData, showDnc);
    };

    const onScrollHandler = useCallback(
        ({ target }) => {
            if (!target || !onReachedEnd) return;
            const { scrollTop, scrollHeight, clientHeight } = target;
            // Calculate scrolled portion. Add THRESHOLD to trigger loading beforehand.
            const scrolledAmount = Number(scrollTop) + Number(clientHeight) + REACHED_END_THRESHOLD;
            if (scrolledAmount - scrollHeight >= 0) onReachedEnd();
        },
        [onReachedEnd]
    );

    const onBulkStatusChange = async (status: TStatus) => {
        setUpdateLoading(true);
        if (isAllSelected && fetchExportData) {
            const data = await fetchExportData();
            const ids = data.map(el => el.propertyLeadId || el.leadId);
            await onUpdateStatuses({ ids, newStatus: status });
        } else {
            const ids = selectedFlatRows.map(row => row.original[Columns.ID]);
            await onUpdateStatuses({ ids, newStatus: status });
        }
        setUpdateLoading(false);
    };

    const renderLoadingRows = (numberOfRows: number) =>
        Array(numberOfRows)
            .fill(null)
            .map((_, rowIndex) => (
                <TableLoadingRow key={`TableLoading-${rowIndex}`}>
                    {headers.map(column => (
                        <TableBodyCell {...column.getHeaderProps()}>
                            <TableLoadingContent />
                        </TableBodyCell>
                    ))}
                </TableLoadingRow>
            ));

    const modalTitle = dataGroup
        ? getLeadsExportModalTitle({
              rowsTotalCount: nrOfLoadedResults ?? 0,
              isAllSelected: Boolean(isAllSelected),
              rowsToBeExported: selectedFlatRows.length ? selectedFlatRows : [],
              dataGroup,
          })
        : `Export ${selectedFlatRows ? selectedFlatRows.length : 0}`;

    return (
        <>
            {renderTableFilter?.()}
            <StyledTable {...containerProps} {...getTableProps()}>
                <TableHead>
                    {headerGroups.map((headerGroup, headerIndex) => (
                        <TableHeadRow {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => {
                                const isSorted = sortParams?.sortBy === column.id && sortParams.sortOrder !== undefined;
                                // selection_placeholder is the name of second row checkbox heading cell
                                const hasSortBy = !column.id.includes('selection_placeholder') && !column.disableSortBy;
                                return (
                                    <TableHeadCell
                                        // this needs to iterate and not only match strings but substrings as well,
                                        // because the second heading row of the table has a different id
                                        rightAligned={tableRightAlignedHeaderCellIds?.reduce<boolean>(
                                            (acc, id) => acc || column.id.includes(id),
                                            false
                                        )}
                                        {...column.getHeaderProps()}
                                    >
                                        <TableHeadData
                                            compact={headerIndex === 0}
                                            clickable={hasSortBy}
                                            aria-selected={hasSortBy ? isSorted : undefined}
                                            selected={isSorted}
                                            onClick={hasSortBy ? () => handleSort(column.id) : undefined}
                                            as={hasSortBy ? 'button' : undefined}
                                        >
                                            <TableHeadTitle>{column.render('Header')}</TableHeadTitle>

                                            {hasSortBy && (
                                                <SortIndicator
                                                    isSorted={isSorted}
                                                    isSortedAsc={isSorted && sortParams.sortOrder === 'asc'}
                                                />
                                            )}
                                        </TableHeadData>
                                    </TableHeadCell>
                                );
                            })}
                        </TableHeadRow>
                    ))}
                </TableHead>
                {isLoadingInitially || updateLoading ? renderLoadingRows(15) : null}
                {tableData.length ? (
                    <TableBody
                        {...getTableBodyProps()}
                        maxHeight={height - top}
                        onScroll={onScrollHandler}
                        ref={tableBodyRef}
                    >
                        {rows.map(row => {
                            prepareRow(row);
                            const id = row.original[Columns.ID].toString();
                            return (
                                <TableBodyRow {...row.getRowProps()}>
                                    {row.cells.map(cell => {
                                        const isName = cell.column.id === clickProps?.columnName;
                                        return (
                                            <TableBodyCell
                                                {...cell.getCellProps()}
                                                clickable={isName}
                                                onClick={isName ? clickProps?.functor(id) : undefined}
                                            >
                                                {cell.render('Cell')}
                                            </TableBodyCell>
                                        );
                                    })}
                                </TableBodyRow>
                            );
                        })}

                        {canLoadMore && renderLoadingRows(3)}
                    </TableBody>
                ) : (
                    <TableEmptyState>{emptyMessage}</TableEmptyState>
                )}
            </StyledTable>

            {!hideFooterElement && (
                <BottomToast
                    text={`${isAllRowsSelected ? nrOfLoadedResults : selectedFlatRows.length} 
                            out of ${nrOfResults} selected`}
                    shown={Boolean(selectedFlatRows.length)}
                    actions={[
                        <Button
                            ref={markAsRef}
                            variant={ButtonVariant.PRIMARY}
                            onClick={() => toggleIsMarkAsOpen()}
                            iconName={IconName.MARK_AS}
                        >
                            Mark as
                        </Button>,
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            onClick={() => setIsExportOpen(true)}
                            iconName={IconName.EXPORT}
                        >
                            Export
                        </Button>,
                    ]}
                />
            )}

            <ExportModal
                isOpen={isExportOpen}
                onClose={() => setIsExportOpen(false)}
                title={modalTitle}
                rowsToBeExported={async () => await preparedExportData()}
            />

            <ControlledMenu onClose={() => toggleIsMarkAsOpen(false)} anchorRef={markAsRef} {...menuProps}>
                {menuStatuses.map((row: TStatus[]) => (
                    <MenuItem key={row[0].toString()} onClick={async () => await onBulkStatusChange(row[0])}>
                        {row[0].toString()}
                    </MenuItem>
                ))}
            </ControlledMenu>

            {!hideHeaderElement &&
                headerElement &&
                createPortal(
                    <>
                        <IconButton
                            disabled={!selectedFlatRows.length}
                            onClick={() => setIsExportOpen(true)}
                            iconName={IconName.EXPORT}
                            label="Export"
                        />
                        <IconButton
                            onClick={() => toggleIsMarkAsOpen()}
                            iconName={IconName.MARK_AS}
                            disabled={!selectedFlatRows.length}
                            label="Mark as"
                            ref={markAsRef}
                        />
                    </>,
                    headerElement
                )}
        </>
    );
}

Table.defaultProps = {
    canLoadMore: false,
    dataGroup: undefined,
    isLoadingInitially: false,
    isAllSelected: false,
    onReachedEnd: undefined,
    onSelectAll: undefined,
    onSortApply: undefined,
    fetchExportData: () => [],
    nrOfResults: 0,
    renderTableFilter: () => null,
    hideHeaderElement: false,
    hideFooterElement: false,
    tableRightAlignedHeaderCellIds: [],
};

